import CloseIcon from '@mui/icons-material/Close';
import {
	Badge,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
} from '@mui/material';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { layoutConfig } from '../config';
import { DrawerItem, WindowSize } from '../types';

interface DrawerComponentProps {
	drawerItems: DrawerItem[];
	navigateWithLanguage: (link: string) => void;
	size: WindowSize;
	logo?: React.ReactNode;
	activeItem?: DrawerItem;
	onClose?: () => void;
	mobileSider?: boolean;
}

export const Drawer = ({
	drawerItems,
	navigateWithLanguage,
	size,
	logo,
	onClose,
	mobileSider,
	activeItem,
}: DrawerComponentProps) => {
	const location = useLocation();
	const isFarmerPath = location.pathname.includes('farmer');
	const { i18n } = useTranslation();
	const isGeorgian = useMemo(() => i18n.language === 'ka', [i18n.language]);
	const isCompactView = size.width <= layoutConfig.largeScreenBreakpoint;

	// Helper function to render icon with badge
	const renderIconWithBadge = (item: DrawerItem) => {
		const icon = item.icon;
		if (item.badge?.show && item.badge.count && item.badge.count > 0) {
			return (
				<Badge
					badgeContent={item.badge.count > 99 ? '99+' : item.badge.count}
					color={item.badge.color || 'error'}
					sx={{
						'& .MuiBadge-badge': {
							fontSize: '10px',
							minWidth: '16px',
							height: '16px',
						},
					}}
				>
					{icon}
				</Badge>
			);
		}
		return icon;
	};

	// Handle touch events for better iOS compatibility
	const handleTouchStart = (event: React.TouchEvent) => {
		// Prevent default to avoid iOS Safari's 300ms delay
		event.preventDefault();
	};

	const handleItemClick = (item: DrawerItem) => {
		if (item.onClick) {
			item.onClick();
		} else {
			navigateWithLanguage(item.link);
		}
		onClose && onClose();
	};

	return (
		<div
			className={classNames('drawer-container z-[9000px]', {
				'px-[40px] py-[20px]': mobileSider,
			})}
		>
			<div className="flex items-center justify-between mb-[7px]">
				{isCompactView ? logo : <div className="flex m-auto pb-[20px]">{logo}</div>}
				{mobileSider && (
					<IconButton 
						onClick={onClose}
						onTouchStart={handleTouchStart}
						sx={{
							cursor: 'pointer',
							WebkitTapHighlightColor: 'transparent',
							WebkitTouchCallout: 'none',
							WebkitUserSelect: 'none',
							userSelect: 'none',
						}}
					>
						<CloseIcon className="text-[#fff]" />
					</IconButton>
				)}
			</div>

			{size.width > layoutConfig.tabletBreakpoint && <Divider className="!border-[#fff]" />}

			{/* Primary menu items */}
			<List className="main-list">
				{drawerItems.map(
					(item, index) =>
						(isFarmerPath || index < 3) && (
							<div key={item.id}>
								<ListItem
									onClick={() => handleItemClick(item)}
									onTouchStart={handleTouchStart}
									disablePadding
									className={classNames({
										'bg-primary max-lg:bg-transparent rounded-[4px]': activeItem?.id === item.id,
									})}
									sx={{
										// iOS-specific fixes
										cursor: 'pointer',
										WebkitTapHighlightColor: 'transparent',
										WebkitTouchCallout: 'none',
										WebkitUserSelect: 'none',
										userSelect: 'none',
										// Ensure proper touch target size for iOS
										minHeight: mobileSider ? '44px' : 'auto',
									}}
								>
									<ListItemButton
										className={classNames({
											'!px-[0px] !py-[35px]': mobileSider,
										})}
										component="a"
										sx={{
											...((!mobileSider && item.style) ? {
												...item.style,
												'&:hover': {
													...item.style,
													opacity: 0.8,
												},
											} : {}),
											// iOS-specific fixes
											cursor: 'pointer',
											WebkitTapHighlightColor: 'transparent',
											WebkitTouchCallout: 'none',
											WebkitUserSelect: 'none',
											userSelect: 'none',
										}}
									>
										{isCompactView ? (
											<Tooltip
												classes={{
													tooltip: isGeorgian ? '!font-nino-mtavruli uppercase' : '',
												}}
												title={item.title}
												placement="right"
											>
												<ListItemIcon
													sx={{
														...((!mobileSider && item.iconStyle) || {}),
													}}
												>
													{renderIconWithBadge(item)}
												</ListItemIcon>
											</Tooltip>
										) : (
											<ListItemIcon
												className="mr-[20px]"
												sx={{
													...((!mobileSider && item.iconStyle) || {}),
												}}
											>
												{renderIconWithBadge(item)}
											</ListItemIcon>
										)}
										<ListItemText
											classes={{
												primary: isGeorgian ? '!font-nino-mtavruli uppercase' : '',
											}}
											primary={item.title}
											sx={
												!mobileSider && item.style
													? { color: item.style.color, fontSize: item.style.fontSize }
													: {}
											}
										/>
									</ListItemButton>
								</ListItem>
								{size.width <= layoutConfig.tabletBreakpoint && (
									<Divider className="!border-[#ffffff33]" />
								)}
							</div>
						)
				)}
			</List>

			{/* Secondary menu items (for non-farmer paths) */}
			{!isFarmerPath && (
				<>
					{size.width > layoutConfig.tabletBreakpoint && <Divider className="!border-white" />}
					<List className="secondary-list">
						{drawerItems.map(
							(item, index) =>
								index >= 3 && (
									<div key={item.id}>
										<ListItem
											onClick={() => handleItemClick(item)}
											onTouchStart={handleTouchStart}
											disablePadding
											className="bg-[#0E776A] max-lg:bg-transparent rounded-[4px]"
											sx={{
												// iOS-specific fixes
												cursor: 'pointer',
												WebkitTapHighlightColor: 'transparent',
												WebkitTouchCallout: 'none',
												WebkitUserSelect: 'none',
												userSelect: 'none',
												// Ensure proper touch target size for iOS
												minHeight: mobileSider ? '44px' : 'auto',
											}}
										>
											<ListItemButton
												className={classNames({
													'!px-[0px] !py-[35px]': mobileSider,
												})}
												component="a"
												sx={{
													...((!mobileSider && item.style) ? {
														...item.style,
														'&:hover': {
															...item.style,
															opacity: 0.8,
														},
													} : {}),
													// iOS-specific fixes
													cursor: 'pointer',
													WebkitTapHighlightColor: 'transparent',
													WebkitTouchCallout: 'none',
													WebkitUserSelect: 'none',
													userSelect: 'none',
												}}
											>
												{isCompactView ? (
													<Tooltip
														classes={{
															tooltip: isGeorgian ? '!font-nino-mtavruli uppercase' : '',
														}}
														title={item.title}
														placement="right"
													>
														<ListItemIcon
															sx={{
																...((!mobileSider && item.iconStyle) || {}),
															}}
														>
															{renderIconWithBadge(item)}
														</ListItemIcon>
													</Tooltip>
												) : (
													<ListItemIcon
														className="mr-[10px]"
														sx={{
															...((!mobileSider && item.iconStyle) || {}),
														}}
													>
														{renderIconWithBadge(item)}
													</ListItemIcon>
												)}
												<ListItemText
													classes={{
														primary: isGeorgian ? '!font-nino-mtavruli uppercase' : '',
													}}
													primary={item.title}
													sx={
														!mobileSider && item.style
															? { color: item.style.color, fontSize: item.style.fontSize }
															: {}
													}
												/>
											</ListItemButton>
										</ListItem>
									</div>
								)
						)}
					</List>
				</>
			)}
		</div>
	);
};
